import {
  AuthProviderProps,
  User,
  UserManager,
  WebStorageStateStore,
} from "oidc-react";
import { registerTokenExpiryHandlers } from "./token-expiry-handlers";
import { b2bReturnUrl, resolveB2bTarget } from "./b2b-return-url";

// Create UserManager instance
export const userManager = new UserManager({
  client_id: window.appConfig.REACT_APP_B2B_IAM_CLIENTID,
  post_logout_redirect_uri: `${window.location.origin}/b2b`,
  redirect_uri: window.location.origin + "/b2b/authentication/callback",
  silent_redirect_uri: window.location.origin + "/b2b/authentication/silent-callback",
  scope: "openid email",
  authority: window.appConfig.REACT_APP_B2B_IAM_AUTHORITY,
  automaticSilentRenew: true,
  loadUserInfo: true,
  includeIdTokenInSilentSignout: true,
  accessTokenExpiringNotificationTimeInSeconds: 120, // Start refresh 2 min before expiry
  silentRequestTimeoutInSeconds: 30, // Allow 30s for silent renewal
  userStore: new WebStorageStateStore({
    store: window.localStorage,
    prefix: "b2b_oidc_" // Separate storage from Lantbrukare
  }),
  metadata: {
    issuer: `${window.appConfig.REACT_APP_B2B_IAM_AUTHORITY}`,
    authorization_endpoint: `${window.appConfig.REACT_APP_B2B_IAM_AUTHORITY}/protocol/openid-connect/auth`,
    token_endpoint: `${window.appConfig.REACT_APP_B2B_IAM_AUTHORITY}/protocol/openid-connect/token`,
    introspection_endpoint: `${window.appConfig.REACT_APP_B2B_IAM_AUTHORITY}/protocol/openid-connect/token/introspect`,
    end_session_endpoint: `${window.appConfig.REACT_APP_B2B_IAM_AUTHORITY}/protocol/openid-connect/logout`,
    revocation_endpoint: `${window.appConfig.REACT_APP_B2B_IAM_AUTHORITY}/protocol/openid-connect/revoke`,
    userinfo_endpoint: `${window.appConfig.REACT_APP_B2B_IAM_AUTHORITY}/protocol/openid-connect/userinfo`,
  },
});

registerTokenExpiryHandlers(userManager, "b2b");

const b2bAuthConfig: AuthProviderProps = {
  onSignIn: async (user: User | null) => {
    // Only redirect on actual OAuth callback (fresh login)
    // Skip redirect for session restore or silent token renewal
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthCode = urlParams.has('code');
    const isCallbackPage = window.location.pathname === '/b2b/authentication/callback';
    const isSilentCallback = window.location.pathname.includes('/silent-callback');

    if (!hasAuthCode || !isCallbackPage || isSilentCallback) {
      // Not a fresh OAuth callback - skip redirect
      return;
    }

    // Fresh login - return to the B2B page the user started from
    window.location.replace(resolveB2bTarget(user?.state));
  },
  onSignInError: (error: Error) => {
    console.error("[B2B Auth] Sign in error:", error);

    // Check if this is a token renewal error (silent renewal) vs. initial sign-in error
    // Token renewal errors should be handled by the event handlers, not here
    const isTokenRenewalError = error.message?.toLowerCase().includes('silent') ||
                                 error.message?.toLowerCase().includes('renew');

    if (isTokenRenewalError) {
      // Token renewal error - don't clear storage, let event handlers retry
      console.warn("[B2B Auth] Token renewal failed, will retry via event handlers");
      return;
    }

    // Initial sign-in or callback error - redirect to login, returning the user
    // to the page they were on rather than dropping them on /b2b
    console.warn("[B2B Auth] Sign-in failed, redirecting to login");
    userManager.signinRedirect({ state: b2bReturnUrl() }).catch(() => {
      window.location.href = "/b2b";
    });
  },
  onBeforeSignIn: b2bReturnUrl,
  autoSignIn: true,
  // oidc-react defaults this to true, which makes AuthProvider register its own
  // silent-renew-error handler that calls signoutRedirect() to Keycloak. That is
  // a second, racing redirect away from the page; recovery is handled by the
  // expiry handler and autoSignIn instead (AG-282).
  autoSignOut: false,
  userManager: userManager,
};

export default b2bAuthConfig;
